import type { RoleName } from '@constants/role_name';

import type { PhoneFormEntry } from '@models/patient';

import type { PlanType } from 'generated/types';

type OnboardingMemberConsent = {
  age: boolean;
  terms: boolean;
  contact: boolean;
};

type OnboardingMember = {
  firstName: string;
  middleName?: string | null;
  lastName: string;
  pricing?: PlanType | null;
  plan?: PlanType | null;
  email?: string | undefined;
  password?: string;
  consents?: OnboardingMemberConsent;
  isPatient: boolean;
  dateOfBirth?: string | null;
  phones?: PhoneFormEntry[];
  role?: string | null;
  /** For org user plans. Should be hidden for non-org/legacy users */
  planId?: string | null;
  questionnaireResponses?: { [linkId: string]: any } | null;
};

/**
 * Drops unanswered questionnaire entries so we don't post empty answers. Every
 * supported question type (string, text, choice) answers with a string, so a
 * falsy value here only ever means "left blank".
 *
 * A truthiness check would wrongly drop `false` or `0`, so if a boolean,
 * integer or decimal item is ever added to the questionnaire this needs to
 * become an explicit empty check. Note `mapValuesToItems` in
 * `hooks/useQuestionnaire.ts` throws on those types today, so it has to be
 * taught them first regardless. See VEG-716.
 */
function pickAnsweredResponses(
  responses: OnboardingMember['questionnaireResponses']
) {
  return Object.fromEntries(
    Object.entries(responses ?? {}).filter(([, value]) => value)
  );
}

/**
 * For non-patient users
 */
type OnboardingUser = {
  email: string;
  firstName: string;
  middleName?: string | null;
  lastName: string;
  roles: { label: RoleName; value: string }[];
  consents?: OnboardingMemberConsent;
};

export { pickAnsweredResponses };
export type { OnboardingMemberConsent, OnboardingMember, OnboardingUser };
